import InputEx from "../../../components/wrapperComponents/InputEx";
import CheckboxEx from "../../../components/wrapperComponents/CheckboxEx";
import React, {useEffect, useMemo, useState} from "react";
import TextAreaEx from "../../../components/wrapperComponents/TextAreaEx";
import RadioInput from "../../../components/RadioInput";
import FormButtons, {TraceManager} from "../../../components/FormButtons";
import RiepilogoIC, {RiepilogoICCompParams} from "../comps/RiepilogoIC";
import ComboBoxCausaliHR from "@components/comboBox/ComboBoxCausaliHR";
import {selectAzienda, selectUser} from "@commons/core.slice";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {UseFormReturn} from "react-hook-form/dist/types";
import {PresenzeCausaleWFRecord} from "@commons/mapping/PresenzeCausaleWF";
import {Periodo} from "@commons/types";
import {UserCRUDRecord} from "@commons/mapping/UserCRUDRecord";
import * as Validation from "@utilities/validationRules";
import {InputCleave2} from "@components/wrapperComponents/InputCleave2";
import {CausaleWFRecord} from "@commons/mapping/CausaleWFRecord";
import {Workflow} from "@commons/mapping/Workflow";
import {useCausaliWF} from "@commons/services/presenze_api/workflow.queries";
import TooltipEx from "@components/Tooltip";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import Select from "@components/wrapperComponents/Select";
import {GiustificativoFilter} from "../GiustificativoFilter";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {LavoratoreBase} from "@commons/mapping/LavoratoreBase";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {isDateBetween, isDateEqual} from "@commons/utils/util";
import {DateTime} from "luxon";
import RangeHH from "./RangeHH";
import {FormProvider} from "react-hook-form";
import {DateTimeUtils} from "@commons/utils/DateTimeUtils";
import {IconInfoSquareRounded} from "@tabler/icons-react";
import {ManutenzioneTimbrature} from "@commons/mapping/ManutenzioneTimbrature";
import {InfoTimbratura} from "@commons/mapping/TimbraturaLavRecord";
import {StrisciataTimbrature} from "../comps/StrisciataTimbrature";
import Banner, {bannerStyle} from "@components/Banner";

export type PageType = 'WF' | 'monitorWF' | 'dashboard' | 'Presenze' | 'Pianificazione'; // N.B. 'Presenze' è utile solo per il prospetto saldo ferie!

export const DashboardDetailWF: React.FC<{
    hook: UseFormReturn<PresenzeCausaleWFRecord>,
    richiesta: PresenzeCausaleWFRecord,
    deleteRichiesta?: boolean,
    onCancel: () => void,
    onSave: (causale: PresenzeCausaleWFRecord) => void,
    periodo: Periodo,
    hhPreviste: number | null, //in minuti
    hhMancanti: number, //in minuti
    hhWeekMedie: number, //in ore
    page: PageType,
    lavoratore: LavoratorePresenzeRecord | LavoratoreBase, // Todo Aggiungere a DashboardWFRecord gli status dei lavoratori!!!
    elencoGiustificativi?: GiustificativoRecord[],
    day?: PresenzeGiornataPlusRecord,
    isEditabile: boolean,

}> = ({
          hook,
          richiesta,
          onCancel,
          onSave,
          periodo,
          deleteRichiesta = false,
          page,
          lavoratore,
          elencoGiustificativi,
          isEditabile,
          ...rest
      }) => {
    const user: UserCRUDRecord | null = useAppSelector(selectUser); // User corrente
    const azienda = useAppSelector(selectAzienda);
    let riepilogoICParams: RiepilogoICCompParams;
    if ("azienda" in lavoratore) {
        riepilogoICParams = {
            idAziendaTabelle: lavoratore?.azienda?.idAziendaCDIC || azienda?.idAziendaCDIC,
            idAziendaMaster: lavoratore?.azienda?.idAziendaMaster || azienda?.idAziendaMaster,
            idDip: lavoratore.id,
            page,
            periodo,
        }
    } else {
        riepilogoICParams = {
            idAziendaTabelle: azienda?.idAziendaCDIC,
            idAziendaMaster: azienda?.idAziendaMaster,
            idDip: lavoratore.id,
            page,
            periodo,
        }
    }

    const [elencoGiuSuStatus, setElencoGiuSuStatus] = useState<GiustificativoRecord[]>([]);

    const [elencoTimbratureOriginaliBkp, setElencoTimbratureOriginaliBkp] = useState<InfoTimbratura[]>([]);
    const [elencoTimbratureOriginali, setElencoTimbratureOriginali] = useState<InfoTimbratura[]>([]);
    const [elencoTimbratureProposte, setElencoTimbratureProposte] = useState<InfoTimbratura[]>([]);

    // TODO: PREVEDERE CALCOLO DELLE PRESENZE!!!!
    const {data: elencoCausaliWF, params} = useCausaliWF();

    const {
        register,
        handleSubmit,
        watch,
        control,
        trigger,
        getValues,
        setValue,
        setError,
        formState: {errors, dirtyFields,},
    } = hook;

    const traceManager = useMemo<TraceManager>(() => {
        return {
            getDeleteInfo: (trace: PresenzeCausaleWFRecord) => {
                return PresenzeCausaleWFRecord.isDeleted(trace) ? PresenzeCausaleWFRecord.getDeleteTrace(trace) : undefined;
            }
        }
    }, []);

    const isMonitor = page === 'monitorWF';
    const isSelfUser = (!isMonitor && richiesta.codiceFiscale === user?.codiceFiscale) && user?.userClass === 'EMPLOYEE';
    const isNew = richiesta.wf.idWF === 0;

    const watchGIntera = watch('giornataIntera');
    const watchElaborata = watch('elaborato');

    const giornoInizio = richiesta.giornoInizio;
    const giornoFine = watch('giornoFine');
    const isSameDay = isDateEqual(giornoInizio, giornoFine);
    const validDateStart = DateTime.fromISO(giornoInizio).toFormat('dd/LL/yyyy');
    const validDateEnd = DateTime.fromISO(DateTimeUtils.getMaxDtGiornoFine(giornoInizio)).toFormat('dd/LL/yyyy');

    const noteApporvatoreLength = (watch('wf.livelli.0.approvatore.note') || '').length;

    const disableFields = !isEditabile || watchElaborata || richiesta.wf.stato === 'RIFIUTATA';

    // N.B. per codiceWF posso usare getValues perchè in tali condizioni non varia mai!
    const codiceWF = getValues('codice'); // Valore del codice causale inserito dal lavoratore, UTILE SOLO nel caso sia un edit successivo al primo caricamento
    const codCausale = watch(page === 'WF' ? 'codice' : 'codiceHR') || codiceWF;
    const causaleRichiesta: CausaleWFRecord | null = elencoCausaliWF ? (elencoCausaliWF.find(causale => causale.codice === codCausale) || null) : null;

    const causaleQuantita: boolean = causaleRichiesta ? causaleRichiesta.tipoCausale === 'QUANTITA' : false;
    const isIntervFacolt: boolean = (codCausale !== null && codCausale !== '') && CausaleWFRecord.isIntervalloFacoltativo(causaleRichiesta);
    const isSoloPerInteraGiornata: boolean = (codCausale !== null && codCausale !== '') && CausaleWFRecord.isSoloPerInteraGiornata(causaleRichiesta);
    const isSoloPerFrazioneGiornata: boolean = (codCausale !== null && codCausale !== '') && CausaleWFRecord.isSoloPerFrazioneGiornata(causaleRichiesta);

    // hook per predisporre l'elenco dei giustificativi filtrati in base agli status del lavoratore e la giornata interessata
    useEffect(() => {
        if (lavoratore && elencoGiustificativi) {
            // Todo Aggiungere a DashboardWFRecord gli status dei lavoratori!!! MA ANCHE I GIUSTIFICATIVI
            // ALTRIMENTI QUI NON CI ENTRA MAI!!!
            if ('status' in lavoratore)
                setElencoGiuSuStatus(elencoGiustificativi.filter(giu => GiustificativoFilter.filterHasStatus(giu, LavoratorePresenzeRecord.getStatus(lavoratore, richiesta.giornoDt))))
        }

        // Dal 11/6/2025 valorizzo strisciate da visualizzare nel form
        if (richiesta && richiesta.tipoCausale === 'MANUTENZIONE_TIMBRATURE') {
            const MT = ManutenzioneTimbrature.factory();
            ManutenzioneTimbrature.ripristinaDaRichiesta(MT, richiesta);
            if (ManutenzioneTimbrature.effettiveOriginaliIsChanged(MT, richiesta)) {
                setElencoTimbratureOriginaliBkp(ManutenzioneTimbrature.updStrisciataOriginaleBkp(richiesta, richiesta.giornoDt));
            }
            setElencoTimbratureOriginali(ManutenzioneTimbrature.updStrisciataOriginale(MT, richiesta.giornoDt));
            setElencoTimbratureProposte(ManutenzioneTimbrature.updStrisciataProposta(MT, richiesta.giornoDt));
        }

    }, [lavoratore, elencoGiustificativi, richiesta]);

    // Hook per flaggare il check di "giornata intera" se l'intervallo di date riguarda più giorni!
    // N.B. <isSameDay> viene testato anche per disabilitare il suddetto check box
    useEffect(() => {
        if (!isSameDay) {
            setValue('giornataIntera', true)
        }
    }, [setValue, isSameDay]);

    /**
     * Hook per flaggare il check di "giornata intera" se la causale selezionata è valida solo per l'intera giornata o
     * per resettare il check di "giornata intera" se la causale selezionata è valida solo per parte della giornata
     * N.B. <isSoloPerInteraGiornata> e <isSoloPerFrazioneGiornata> vengono testati anche per disabilitare il suddetto check box
     */
    useEffect(() => {
        if (!causaleQuantita) {
            if (isSoloPerInteraGiornata) {
                setValue('giornataIntera', true);
                onChangeGiornataIntera(true);
            } else if (isSoloPerFrazioneGiornata && isSameDay) {
                setValue('giornataIntera', false);
                onChangeGiornataIntera(false);
            }
        }
    }, [setValue, isSoloPerInteraGiornata, isSoloPerFrazioneGiornata, causaleQuantita, isSameDay]);

    // Wrapper per funzione globale
    const validaMonteOreMancante = (quantitaRichiesta: number) => {
        return CausaleWFRecord.verificaMonteOreMancante(causaleRichiesta, watchGIntera, isSameDay, quantitaRichiesta, rest);
    }

    // Forza validazione per campo quantità in caso di valore nn valido!
    const verificaMonteOreMancante = (quantitaRichiesta: number): void => {
        const result = CausaleWFRecord.verificaMonteOreMancante(causaleRichiesta, watchGIntera, isSameDay, quantitaRichiesta, rest);
        if (!result.isValidQuantita || !result.isValidQuantitaParziale) {
            setError('quantita', {
                type: !result.isValidQuantita ? 'quantita' : 'quantitaParziale'
            });
            trigger('quantita');
        }
        return
    }

    // Hook per la validazione della quantità all'atto della "apertura" del form
    // Utile per quei casi marginali dove la quantità della richiesta NON è valida (es. cambio previsto dopo caricamento della richiesta)
    useEffect(() => {
        if (!deleteRichiesta) // se non sto cancellando la richiesta
            if (richiesta && richiesta.wf.idWF) // se <richiesta> caricata e non si tratta di un nuovo caricamento
                if (Object.keys(errors).length === 0 // se non ci sono errori
                    && Object.keys(dirtyFields).length === 0  // se non ho editato alcun campo
                    && richiesta.quantita === getValues('quantita') // se lo "stato" del form è consolidato (valorizzazione degli INPUT completata)
                ) {
                    // console.log("effect apertura form");
                    verificaMonteOreMancante(richiesta.quantita);
                }
    }, [richiesta, errors, dirtyFields, getValues, setError, trigger, verificaMonteOreMancante, deleteRichiesta]);

    const onChangeGiornataIntera = (giornataIntera: boolean) => {
        if (giornataIntera) {
            setValue('oraInizio', '0000');
            setValue('oraFine', '0000');

            if (rest.hhPreviste !== null) {
                const quantitaInput = rest.hhPreviste / 60;
                setValue('quantita', quantitaInput, {shouldValidate: true});

                verificaMonteOreMancante(quantitaInput);
            }
        }
    }

    const isCausaleValid = (codCausale: string) => {
        if (codCausale && elencoCausaliWF) {
            const causale = elencoCausaliWF.find(causale => causale.codice === codCausale) || null;
            return !CausaleWFRecord.checkPiuGiorni(causale, giornoInizio, giornoFine);
        } else return true;
    }

    const onSubmit = (causale: PresenzeCausaleWFRecord) => {
        onSave({...causale, idTabella: causaleRichiesta!.idTabella}); // todo Migliorare la valorizzazione di idTabella!
    };

    return <FormProvider {...hook}>
        <form className="bg-white shadow-md rounded p-2 space-y-3 xl:min-w-[1130px]" onSubmit={handleSubmit(onSubmit)}>
            {/* ******************************** RICHIESTA ******************************* */}
            <fieldset className="border border-gray-200 p-2 space-y-3" disabled={richiesta.wf.annullata || deleteRichiesta}>
                <legend className="px-2">Richiesta</legend>

                {/* DATE DI RIFERIMENTO RICHIESTA - Visualizzate solo se si sta inserendo la richiesta dalla pagina WORKFLOW */}
                {page === "WF" && <div className="md:flex md:space-x-2 2xl:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-1/6">
                        <InputEx
                            label="Dal"
                            type="date"
                            name="giornoInizio"
                            register={register}
                            disabled
                        />
                    </div>
                    <div className="basis-1/6">
                        <InputEx
                            label="Al"
                            type="date"
                            name="giornoFine"
                            register={register}
                            registerOptions={{
                                required: "Campo obbligatorio",
                                validate: {
                                    isBetween: v => isDateBetween(giornoInizio, DateTimeUtils.getMaxDtGiornoFine(giornoInizio), v) || `La data dev'essere compresa tra ${validDateStart} e ${validDateEnd}`
                                }
                            }}
                            errors={errors}
                            disabled={!isNew}
                        />
                    </div>
                </div>}

                <div className="md:flex md:space-x-2 2xl:space-x-4 space-y-2 md:space-y-0">
                    {!isNew && page !== "WF" && <div className="basis-1/6">
                        <InputEx
                            label="Data"
                            type="date"
                            name="giornoDt"
                            register={register}
                            disabled
                        />
                    </div>}

                    <div className={`${page === 'WF' ? 'basis-1/3' : 'basis-1/6 md:basis-1/4'}`}>
                        {!isNew && <InputEx
                            label="Causale lavoratore"
                            type="text"
                            name="descrizioneCausale"
                            register={register}
                            disabled={true}
                        />}

                        {isNew && <>
                            <input type="hidden" name="descrizioneCausale" {...register}/>
                            <Select label={
                                <span className="inline-flex">Causale&nbsp;
                                    <TooltipEx
                                        tooltip={<>N.B. Alcune causali potrebbero non essere disponibili<br/>perchè applicabili solo a preventivo o consuntivo!</>}
                                        children={<IconInfoSquareRounded className="text-danger w-5 h-5"/>}/>
                                </span>}
                                    id="causaleRichiesta" name="codice" register={register} registerOptions={
                                {
                                    required: "Campo obbligatorio",
                                    validate: {
                                        enabled: (v: string) => isCausaleValid(v) || "Causale non valida"
                                    }
                                }
                            } errors={errors}
                                    children={(elencoCausaliWF && richiesta.wf.idWF !== null) && elencoCausaliWF
                                        .filter(causale => causale.tipoCausale !== 'MANUTENZIONE_TIMBRATURE')
                                        .filter(causale => CausaleWFRecord.filterAmbitoTemporale(causale, richiesta.giornoDt))
                                        .filter(causale => CausaleWFRecord.filterHasStatus(causale, elencoGiuSuStatus))
                                        .filter(causale => CausaleWFRecord.filterHasPrevisto(causale, rest.hhPreviste))
                                        .filter(causale => CausaleWFRecord.filterCheckIstitutoContrattuale(causale, lavoratore))
                                        .map((causale) => {
                                            return <option key={causale.codice} value={causale.codice}
                                                           disabled={
                                                               CausaleWFRecord.checkPiuGiorni(causale, giornoInizio, giornoFine) ||
                                                               (rest.day && !CausaleWFRecord.checkDayCompliance(causale, elencoGiustificativi, rest.day))
                                                           }>
                                                {causale.descrizione} [#{causale.codice}]
                                            </option>
                                        })}
                                    onChange={(v) => {
                                        const causale = elencoCausaliWF?.find(causale => causale.codice === v.target.value) || null;
                                        setValue('descrizioneCausale', causale ? causale.descrizione : '???');
                                    }}/>
                        </>}
                    </div>

                    {/* *** INIZIO *** 'ORE_MANCANTI' | 'ORE_ECCEDENTI' | 'QUANTITA'*/}
                    {causaleRichiesta?.tipoCausale !== 'MANUTENZIONE_TIMBRATURE' && <>
                        {!causaleQuantita && <div className="basis-1/6 flex items-center justify-center">
                            <CheckboxEx label="Giornata intera" name="giornataIntera" control={control}
                                        disabled={disableFields || isSoloPerInteraGiornata || isSoloPerFrazioneGiornata || !isSameDay} onToggle={onChangeGiornataIntera}/>
                        </div>}

                        {watchGIntera && <>
                            <div className="basis-1/6">&nbsp;</div>
                            <div className="basis-1/6">&nbsp;</div>
                            <div className="basis-1/6">
                                <input type="hidden" {...register('quantita', {
                                    validate: {
                                        quantita: v => validaMonteOreMancante(v).isValidQuantita || 'Ore totali di assenza o fuori sede maggiori delle ore previste!',
                                        quantitaParziale: v => validaMonteOreMancante(v).isValidQuantitaParziale || `La quantità dell'intervallo deve essere minore delle ore previste!`,
                                    }
                                })}/>
                            </div>
                        </>}

                        {!watchGIntera && !isSoloPerInteraGiornata && <>
                            {!causaleQuantita && <>
                                <RangeHH<PresenzeCausaleWFRecord, 'quantita'> control={control}
                                                                              trigger={trigger}
                                                                              getValues={getValues}
                                                                              setValue={setValue}
                                                                              nameInizio="oraInizio"
                                                                              nameFine="oraFine"
                                                                              nameQuantita="quantita"
                                                                              validate={{
                                                                                  range: v => (CausaleWFRecord.isRangeValid(causaleRichiesta!, v as number) || CausaleWFRecord.getRangeText2021(causaleRichiesta!)),
                                                                                  quantita: v => validaMonteOreMancante(v).isValidQuantita || 'Ore totali di assenza o fuori sede maggiori delle ore previste!',
                                                                                  quantitaParziale: v => validaMonteOreMancante(v).isValidQuantitaParziale || `La quantità dell'intervallo deve essere minore delle ore previste!`,
                                                                              }}
                                                                              errorPortalId="errorQuantita2"
                                    //onChangeRange={(inizio, fine, durata) => verificaMonteOreMancante(durata)}
                                                                              disableFields={disableFields}
                                                                              optionalFields={isIntervFacolt}>
                                    {(hhInizio, hhFine, quantita) => {
                                        return <>
                                            <div className="basis-1/6">
                                                {hhInizio}
                                            </div>
                                            <div className="basis-1/6">
                                                {hhFine}
                                            </div>
                                            <div className="basis-1/6">
                                                {quantita}
                                            </div>
                                        </>
                                    }
                                    }
                                </RangeHH>
                            </>}
                            {causaleQuantita && <>
                                <div className="basis-1/6">
                                    <InputCleave2<PresenzeCausaleWFRecord>
                                        options={{
                                            allowNegative: false, thousandSeparator: '\'', allowedDecimalSeparators: [','], fixedDecimalScale: false, decimalScale: 2,
                                        }}
                                        placeholder="Indicare un importo o quantità"
                                        rules={{
                                            validate: {
                                                validaRange: v => CausaleWFRecord.isRangeValid(causaleRichiesta!, v as number) || CausaleWFRecord.getRangeText2021(causaleRichiesta!)
                                            }
                                        }}
                                        label="Importo/Quantit&agrave;"
                                        name="quantita"
                                        disabled={disableFields}
                                        control={control}
                                        errorPortalId={'errorQuantita'}
                                    />
                                </div>
                            </>}
                        </>}
                    </>}
                    {/* *** FINE *** 'ORE_MANCANTI' | 'ORE_ECCEDENTI' | 'QUANTITA'*/}
                </div>
                {/* *** INIZIO *** 'MANUTENZIONE_TIMBRATURE'*/}
                {causaleRichiesta?.tipoCausale === 'MANUTENZIONE_TIMBRATURE' && <div className="width">
                    <StrisciataTimbOriginaliBkp timbrature={elencoTimbratureOriginaliBkp}></StrisciataTimbOriginaliBkp>
                    <StrisciataTimbrature timbrature={elencoTimbratureOriginali}
                                          title="Timbrature originali:" titleIfNull="Nessuna timbratura"></StrisciataTimbrature>
                    <StrisciataTimbrature timbrature={elencoTimbratureProposte}
                                          title="Timbrature proposte:"></StrisciataTimbrature>
                </div>}
                {/* *** FINE *** 'MANUTENZIONE_TIMBRATURE'*/}

                <div className="md:flex md:space-x-2 2xl:space-x-4 space-y-1 md:space-y-0">
                    <div className="basis-1/6"/>
                    <div className="basis-1/6 md:basis-1/4"/>
                    <div id="errorQuantita"/>
                </div>
                <div className="md:flex md:space-x-2 2xl:space-x-4 space-y-1 md:space-y-0">
                    <div className="basis-1/6"/>
                    <div className="basis-1/6 md:basis-1/4"/>
                    {!watchGIntera && <div id="errorQuantita2"/>}
                    {watchGIntera && <div id="errorQuantita3" className="text-danger">{(errors && errors.quantita) ? errors.quantita.message : null}</div>}
                </div>

                {(!isNew && page !== 'WF' && (causaleRichiesta?.tipoCausale !== 'MANUTENZIONE_TIMBRATURE')) && <div className="md:flex md:space-x-2 2xl:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-1/3">
                        <ComboBoxCausaliHR<PresenzeCausaleWFRecord>
                            apiParams={params}
                            control={control}
                            name="codiceHR"
                            label={'Causale responsabile/HR'}
                            disabled={disableFields || !isSameDay}
                            auxFilter={v => v.tipoCausale === richiesta.tipoCausale}
                            onChange={(causaleRichiesta: CausaleWFRecord | null) => {
                                /*const {isValidQuantita, isValidQuantitaParziale} = _verificaMonteOreMancante(causaleRichiesta,
                                    watchGIntera,
                                    isSameDay,
                                    getValues('quantita'),
                                    rest);*/
                                trigger('quantita');
                            }}
                        />
                    </div>
                </div>}

                <div className="md:flex md:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-full">
                        <TextAreaEx<PresenzeCausaleWFRecord> rows={4} label="Note richiedente" name="wf.livelli.0.richiedente.note" register={register} disabled={!isNew}
                                                             registerOptions={{
                                                                 maxLength: 128,
                                                                 validate: {required: (v: string) => CausaleWFRecord.isNoteValid(causaleRichiesta, 'richiedente', v) || 'Campo obbligatorio'}
                                                             }}
                                                             errors={errors}
                                                             addTextLength={true} maxLength={128}/>

                    </div>
                </div>

                {!isNew && <div className="md:flex md:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-2/6">
                        <InputEx
                            label="Richiesta inserita da"
                            type="text"
                            name="insertTrace.userName"
                            register={register}
                            disabled/>
                    </div>
                    <div className="basis-2/6">
                        <InputEx
                            label="Richiesta inserita il"
                            type="datetime-local"
                            name="insertTrace.timeStamp"
                            register={register}
                            disabled/>
                    </div>
                    <div className="basis-2/6 flex items-center">
                        <CheckboxEx label="Elaborata" name="elaborato" control={control}
                                    disabled={!watchElaborata || richiesta.wf.stato === 'RIFIUTATA' || user!.userClass === 'EMPLOYEE'}/>
                    </div>
                </div>}
            </fieldset>

            {/* ******************************** RIEPILOGO FERIE E PERMESSI ******************************* */}
            {!isSelfUser
                && !isNew // modifica o visualizzazione
                && page !== 'WF' // monitor o dashboard
                && richiesta.tipoCausale !== 'MANUTENZIONE_TIMBRATURE' // non per causale per manutenzione timbrature
                && <fieldset className="border border-gray-200 p-4">
                    <legend className="px-2">Ferie e permessi</legend>
                    <div className="w-1/2">
                        <RiepilogoIC {...riepilogoICParams}/>
                    </div>
                </fieldset>}

            {/* ******************************** FLUSSO AUTORIZZATIVO ******************************* */}
            {!isSelfUser && <fieldset className="border border-gray-200 p-2 space-y-4"
                                      disabled={richiesta.wf.annullata || deleteRichiesta || !isEditabile}>
                <legend className="px-2">Flusso autorizzativo #0</legend>

                <div className="md:flex md:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-1/6">
                        <label htmlFor="tipo" className="mb-1 block font-medium text-sm">Stato richiesta</label>
                        <RadioInput
                            label="In valutazione"
                            name="wf.livelli.0.stato"
                            id="inValutazione"
                            register={register}
                            registerOptions={{required: noteApporvatoreLength > 0 || isNew}}
                            value="IN_VALUTAZIONE"
                            disabled={richiesta.wf.stato === "ACCETTATA" || richiesta.wf.stato === "RIFIUTATA"}
                        />
                        <RadioInput
                            label="Accettata"
                            name="wf.livelli.0.stato"
                            id="accettata"
                            register={register}
                            registerOptions={{required: noteApporvatoreLength > 0 || isNew}}
                            value="ACCETTATA"
                            disabled={richiesta.wf.stato === "RIFIUTATA"}
                        />
                        {!isNew && <RadioInput
                            label="Respinta"
                            name="wf.livelli.0.stato"
                            id="RIFIUTATA"
                            register={register}
                            registerOptions={{required: noteApporvatoreLength > 0}}
                            value="RIFIUTATA"
                            disabled={richiesta.wf.stato === "ACCETTATA"}
                        />}
                        {(((!isNew && noteApporvatoreLength > 0) || isNew) && (errors && errors.wf?.livelli && errors.wf?.livelli[0]?.stato) &&
                            <p className="text-danger">Indicare uno stato!</p>)}
                    </div>
                    <div className="basis-5/6">
                        <TextAreaEx<PresenzeCausaleWFRecord> rows={4} label="Note" name="wf.livelli.0.approvatore.note" register={register}
                                                             disabled={richiesta.wf.annullata || deleteRichiesta}
                                                             registerOptions={{
                                                                 maxLength: 128,
                                                                 validate: {required: (v: string) => CausaleWFRecord.isNoteValid(causaleRichiesta, 'approvatore', v) || 'Campo obbligatorio'},
                                                             }}
                                                             errors={errors}
                                                             addTextLength={true} maxLength={128}/>
                    </div>
                </div>
                {(richiesta.wf.livelli && richiesta.wf.livelli[0].approvatore && !isNew) &&
                    <div className="grid grid-cols-6 gap-3">
                        <div className="basis-1/6 col-span-2">
                            <InputEx<PresenzeCausaleWFRecord>
                                label={`Richiesta ${Workflow.getDescrizioneStato(richiesta.wf.stato)} da`}
                                type="text"
                                name="wf.livelli.0.approvatore.userName"
                                register={register}
                                disabled/>
                        </div>
                        <div className="basis-1/6 col-span-2">
                            <InputEx<PresenzeCausaleWFRecord>
                                label={`Richiesta ${Workflow.getDescrizioneStato(richiesta.wf.stato)} il`}
                                type="datetime-local"
                                name="wf.livelli.0.approvatore.timeStamp"
                                register={register}
                                disabled/>
                        </div>
                    </div>
                }
            </fieldset>}

            {/* ******************************** INVALIDA RICHIESTA ******************************* */}
            {(richiesta.wf.annullata || deleteRichiesta) && <fieldset className="border border-gray-200 p-2 space-y-4">
                <legend className="px-2">{richiesta.wf.annullata ? 'Stato richiesta' : 'Invalida richiesta'}</legend>

                <div className="md:flex md:space-x-4 space-y-2 md:space-y-0">
                    <div className="basis-1/6">
                        <CheckboxEx name="wf.annullata" disabled
                                    control={control}
                                    label="Richiesta invalidata"/>
                    </div>
                    <div className="basis-5/6">
                        <TextAreaEx<PresenzeCausaleWFRecord> rows={4} label="Note" name="wf.noteAnnullata" register={register}
                                                             addTextLength={true} maxLength={128} disabled={richiesta.wf.annullata}
                                                             registerOptions={{maxLength: 128, ...Validation.required}} errors={errors}
                                                             placeholder="Indicare il motivo per cui si invalida la richiesta"/>
                    </div>
                </div>
            </fieldset>}

            <FormButtons resource={richiesta}
                         onCancel={onCancel}
                         readonly={(!isNew && !deleteRichiesta && (richiesta.wf.annullata || isSelfUser || !isEditabile))}
                         traceManager={traceManager}/>

        </form>
    </FormProvider>
}

const StrisciataTimbOriginaliBkp: React.FC<{ timbrature: InfoTimbratura[] }> = ({timbrature}) => {
    if (!timbrature || timbrature.length === 0)
        return null;
    return <>
        <Banner style={bannerStyle.danger} text="Le timbrature attuali sono diverse da quelle valutate al momento del caricamento della richiesta!"/>

        <StrisciataTimbrature timbrature={timbrature}
                              title="Timbrature attuali:"></StrisciataTimbrature>
    </>
}

export default DashboardDetailWF;